import { useState } from "react";
import { ActiveTest } from "@/admin/test/ActiveTest";
import { CreateTestDialog } from "@/components/car-test/CreateTestDialog";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import CreateTestButton from "@/components/car-test/CreateTestButton";
import { Input } from "@/components/ui/input";
import { AnimatePresence, motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ColoredOutlineButton,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { generateVehicleTestPdf } from "@/utils/pdfGenerator";
import { VehicleTestDocumentView } from "@/admin/pages/VehicleTestDocumentView";
import { useTest } from "@/hooks/useTest";
import { VehicleTest } from "@/types/vehicleTest";
import { toast } from "sonner";
import { PdfGenerationDialog } from "@/components/ui/PdfGenerationDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n/labels";
import { cn } from "@/lib/utils";
import { ArchiveRestore, Check, ClipboardList } from "lucide-react";

function mapVehicleTestToDocumentData(test: VehicleTest) {
  return test;
}

const CarTestArchive = () => {
  const [viewTest, setViewTest] = useState<VehicleTest | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<{
    isOpen: boolean;
    testName?: string;
    fileName?: string;
  }>({
    isOpen: false,
  });
  const {
    archivedTests,
    isLoadingArchivedTests,
    refetchArchivedTests,
    unarchiveCarTests,
    isUnarchivingCarTests,
  } = useTest({ disableAdminQuery: true, tab: "archived" });

  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [isUnarchiveMode, setIsUnarchiveMode] = useState(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);

  const handleCreateTest = () => {
    setIsCreateDialogOpen(true);
  };

  const handleDownloadPdf = async (test: VehicleTest) => {
    setIsGeneratingPdf({
      isOpen: true,
      testName: test.vehicleName,
      fileName: `fordonsprotokoll-${test.vehicleDetails?.regNumber || test.id}.pdf`,
    });
    try {
      await generateVehicleTestPdf(test, language);
      toast.success(ct.messages.pdfGenerated);
    } catch {
      toast.error(ct.messages.pdfError);
    } finally {
      setIsGeneratingPdf({ isOpen: false });
    }
  };

  const handleUnarchive = async (testIds: string[]) => {
    try {
      await unarchiveCarTests(testIds);
      toast.success(ct.archive.unarchived);
      refetchArchivedTests();
      setSelectedTests(new Set());
      setIsUnarchiveMode(false);
    } catch {
      toast.error(ct.archive.unarchiveError);
    }
  };

  const toggleUnarchiveMode = () => {
    setIsUnarchiveMode(!isUnarchiveMode);
    setSelectedTests(new Set());
  };

  const unarchiveToggleLabel = () => {
    if (isUnarchiveMode) return ct.archive.exitUnarchiveMode;
    return isMobile
      ? ((ct.archive as { unarchiveModeShort?: string }).unarchiveModeShort ??
          ct.archive.unarchiveMode)
      : ct.archive.unarchiveMode;
  };

  const actionBtnClass = cn(
    "h-8 px-2.5 text-xs gap-1.5 justify-center whitespace-nowrap",
    isMobile ? "flex-1 min-w-0 w-full" : "shrink-0 w-auto",
  );

  const renderBackToTestsButton = (opts?: { fullWidth?: boolean }) => (
    <ColoredOutlineButton
      type="button"
      variant="blue"
      tintedBackground
      size="sm"
      onClick={() => navigate("/car-test")}
      className={cn(
        "h-8 px-2.5 text-xs gap-1.5 justify-center whitespace-nowrap",
        opts?.fullWidth ? "flex-1 min-w-0 w-full" : "shrink-0 w-auto",
      )}
    >
      <ClipboardList
        className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
        strokeWidth={2.35}
      />
      <span className="truncate">
        {isMobile ? ct.archive.backToTestsMobile : ct.archive.backToTests}
      </span>
    </ColoredOutlineButton>
  );

  if (isLoadingArchivedTests) {
    return (
      <div>
        {!isMobile && <BtnNavigateBack label="Dashboard" />}
        <div className="flex justify-center mb-4">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">
            {ct.archive.title}
          </h1>
        </div>
        <div className="container max-w-4xl mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">{ct.archive.loading}</div>
          </div>
        </div>
      </div>
    );
  }

  const showConfirm = isUnarchiveMode && selectedTests.size > 0;

  return (
    <div>
      <div className="container max-w-4xl mx-auto px-4">
        <div className="relative mb-4">
          {isMobile ? (
            <div className="flex justify-center items-center">
              <h1 className="text-xl font-bold tracking-tight">
                {ct.archive.title}
              </h1>
            </div>
          ) : (
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <BtnNavigateBack
                label="Dashboard"
                className="mb-0 shrink-0 justify-self-start"
              />
              <h1 className="text-xl md:text-2xl font-bold tracking-tight text-center px-2">
                {ct.archive.title}
              </h1>
              <div />
            </div>
          )}
        </div>

        {isMobile && (
          <div className="flex items-center mb-3">
            <CreateTestButton onClick={handleCreateTest} />
          </div>
        )}

        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
          <Input
            placeholder={ct.search.placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="backdrop-blur w-full text-[10px] sm:text-sm h-8 sm:h-10 md:min-w-0 md:flex-1"
          />
          <div
            className={cn(
              "flex gap-2 items-stretch",
              isMobile ? "w-full" : "w-auto shrink-0 flex-wrap justify-end",
            )}
          >
            <ColoredOutlineButton
              type="button"
              variant="neutral"
              tintedBackground
              size="sm"
              onClick={toggleUnarchiveMode}
              className={actionBtnClass}
            >
              <ArchiveRestore
                className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                strokeWidth={2.35}
              />
              <span className="truncate">{unarchiveToggleLabel()}</span>
            </ColoredOutlineButton>

            {showConfirm ? (
              <ColoredOutlineButton
                type="button"
                variant="success"
                tintedBackground
                size="sm"
                onClick={() => setShowUnarchiveDialog(true)}
                className={actionBtnClass}
              >
                <Check
                  className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                  strokeWidth={2.35}
                />
                <span className="truncate">
                  {ct.archive.unarchiveSelected.replace(
                    "{count}",
                    String(selectedTests.size),
                  )}
                </span>
              </ColoredOutlineButton>
            ) : null}

            {isMobile && !showConfirm
              ? renderBackToTestsButton({ fullWidth: true })
              : null}

            {!isMobile && (
              <>
                {renderBackToTestsButton()}
                <CreateTestButton onClick={handleCreateTest} />
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <AnimatePresence>
            {archivedTests && archivedTests.length > 0 ? (
              archivedTests
                .filter((test) => {
                  const searchLower = search.toLowerCase();
                  return (
                    (
                      test.vehicleDetails.regNumber?.toLowerCase() || ""
                    ).includes(searchLower) ||
                    (test.vehicleName?.toLowerCase() || "").includes(
                      searchLower,
                    )
                  );
                })
                .map((test) => (
                  <motion.div
                    key={test.id}
                    className={cn(
                      "relative",
                      isUnarchiveMode && "cursor-pointer",
                    )}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 200 }}
                    transition={{ duration: 0.5 }}
                    onClick={() => {
                      if (isUnarchiveMode) {
                        const next = new Set(selectedTests);
                        if (next.has(test.id)) next.delete(test.id);
                        else next.add(test.id);
                        setSelectedTests(next);
                      }
                    }}
                  >
                    <div
                      className={cn(
                        "flex items-start gap-3",
                        isUnarchiveMode && "mb-2",
                      )}
                    >
                      {isUnarchiveMode && (
                        <input
                          type="checkbox"
                          checked={selectedTests.has(test.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            const next = new Set(selectedTests);
                            if (e.target.checked) next.add(test.id);
                            else next.delete(test.id);
                            setSelectedTests(next);
                          }}
                          className="w-5 h-5 mt-2 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div className="flex-1">
                        <ActiveTest
                          key={test.vehicleDetails.regNumber}
                          test={test}
                          onView={() => setViewTest(test)}
                          onDownload={handleDownloadPdf}
                          isAdmin={false}
                          selectionMode={isUnarchiveMode}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                {ct.archive.noArchivedTests}
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <CreateTestDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      {viewTest && (
        <Dialog open={!!viewTest} onOpenChange={() => setViewTest(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {[
                  viewTest.vehicleDetails?.make,
                  viewTest.vehicleDetails?.model,
                  viewTest.vehicleDetails?.year,
                ]
                  .filter(Boolean)
                  .join(" ") ||
                  ct.dialogs.testProtocol.title.replace("{vehicleName}", "")}
              </DialogTitle>
            </DialogHeader>
            <VehicleTestDocumentView
              data={mapVehicleTestToDocumentData(viewTest)}
            />
          </DialogContent>
        </Dialog>
      )}

      <PdfGenerationDialog
        isOpen={isGeneratingPdf.isOpen}
        testName={isGeneratingPdf.testName}
        fileName={isGeneratingPdf.fileName}
      />

      <Dialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ct.archive.unarchiveDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              {ct.archive.unarchiveDialog.message.replace(
                "{count}",
                String(selectedTests.size),
              )}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUnarchiveDialog(false)}
            >
              {ct.archive.unarchiveDialog.cancel}
            </Button>
            <Button
              onClick={() => {
                handleUnarchive(Array.from(selectedTests));
                setShowUnarchiveDialog(false);
              }}
              disabled={isUnarchivingCarTests}
            >
              {ct.archive.unarchiveDialog.confirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarTestArchive;
